// The underlyings a headline names outright: a coin, a company, a commodity, an index, a currency,
// a bond, by name or by ticker. Each name is a pattern over the headline; where two overlap, the one
// that starts first wins, then the longer ("Bitcoin Cash" over "Bitcoin", "Brent crude" over
// "crude"). Names are tried only when the headline holds their first word.
//
// How names are written in the table:
//   "bitcoin"      the words, case aside, whole words (white space in the name: any run of spaces
//                  and hyphens; "&" may have spaces around it).
//   "=SOL"         a ticker written in capitals, standalone. Never in a shouted headline.
//   "~DOT"         a ticker that is also an English word or a common acronym: in capitals, and only
//                  when the headline is about its market (crypto words for a coin, share words for a
//                  stock).
//   "^Apple"       the name as written (a capital), or all in capitals.
//   { re }         a pattern of its own, case aside unless `cs`; `ctx` as for "~"; `head` the word
//                  it needs (without one it is tried on every headline).
// A coin's ticker also counts before "token" or "coin", and before "price" when capitalised.
// A `$cashtag` always counts (atlas `ticker_mentions`), for any symbol.

import type { Reading } from "./places.ts";
import { canonical, classOf } from "./symbols.ts";
import { headOf, phraseSource, shouted, wordsOf } from "./text.ts";

type Rx = { re: string; cs?: true; ctx?: Ctx; head?: string; upper?: true };
type Spec = string | Rx;
type Ctx = "crypto" | "equity" | "intel";

const CONTEXT: Readonly<Record<Ctx, RegExp>> = {
  crypto:
    /\b(?:crypto\w*|bitcoin|btc|ether|ethereum|eth|tokens?|coins?|memecoins?|meme coins?|altcoins?|blockchains?|defi|web3|staking|staked|airdrops?|mainnet|testnet|layer[- ]?[12]|dex|stablecoins?|tether|usdt|usdc|nfts?|binance|coinbase|polkadot|cardano|justin sun|on-?chain|validators?|hyperliquid|solana|tvl|dapps?|smart contracts?|hard fork|halving|etfs?)\b/i,
  equity:
    /\b(?:shares?|stocks?|earnings|nasdaq|nyse|ticker|ipo|guidance|analysts?|downgraded?|upgraded?|price target|market (?:cap|value)|valuation|quarterly|revenue|profits?|investors?|buybacks?|dividends?|s&p 500)\b/i,
  intel:
    /\b(?:chips?|chipmakers?|foundry|foundries|semiconductors?|processors?|cpus?|shares|stocks?|earnings|corp|nasdaq|ceo|lip-bu tan|gelsinger|x86|fabs?)\b/i,
};

const S = String.raw;
/** How a headline says a price moved. */
const MOVE = S`rises|rose|falls|fell|slips|slipped|gains|gained|jumps|jumped|slides|slid|weakens|weakened|strengthens|strengthened|firms|firmed|eases|eased|extends|steadies|rall(?:y|ies|ied)|tumbles|tumbled|sinks|sank|surges|surged|climbs|climbed|edges|holds|hovers|drops|dropped|hits|plunges|plunged|soars|soared|retreats|rebounds|slumps|slumped`;
const MOVES = `(?:${MOVE})`;
/** Ten-year yields that are not Treasuries. */
const NOT_US = S`(?<!(?:japan(?:'s|ese)?|german|gilt|bund|jgb|uk|british|britain's|italian|french|china's|chinese|indian|australian|canadian|korean|euro(?:pean)?|swiss)\s)`;

/** Canonical underlying, then the names it goes by. */
const NAMES: readonly (readonly [string, ...Spec[]])[] = [
  /* ── crypto ── */
  ["BTC", "bitcoin", "btc", "=XBT"],
  ["BCH", "bitcoin cash", "=BCH"],
  ["BSV", "bitcoin sv", "=BSV"],
  ["ETH", "ethereum", "ether", "eth"],
  ["ETC", "ethereum classic", "~ETC"],
  ["SOL", "solana", "=SOL"],
  ["XRP", "xrp", "^Ripple"],
  ["DOGE", "dogecoin", "~DOGE"],
  ["HYPE", "hyperliquid", "=HYPE"],
  ["BNB", "bnb", "binance coin"],
  ["ADA", "cardano", "~ADA"],
  ["LINK", "chainlink", "=LINK"],
  ["AVAX", "avax", { re: S`(?<!\w)avalanche(?!\w)`, ctx: "crypto", head: "avalanche" }],
  ["SUI", "=SUI", { re: S`(?<!\w)Sui(?=[\s-]+(?:[Nn]etwork|[Bb]lockchain|[Ff]oundation|[Ee]cosystem|[Mm]ainnet)\b)`, cs: true, head: "sui" }],
  ["TRX", "=TRX", { re: S`(?<!\w)tron(?!\w)`, ctx: "crypto", head: "tron" }],
  ["TON", "toncoin", "=TON", "the open network"],
  ["LTC", "litecoin", "=LTC"],
  ["DOT", "polkadot", "~DOT"],
  ["PEPE", "=PEPE", "=kPEPE", "=1000PEPE", "pepecoin"],
  ["SHIB", "shiba inu", "shib", "=kSHIB"],
  ["ENA", "ethena", "=ENA"],
  ["AAVE", "aave"],
  ["UNI", "uniswap", "=UNI"],
  ["NEAR", "=NEAR", "near protocol"],
  ["APT", "aptos", "=APT"],
  ["ARB", "arbitrum", "=ARB"],
  ["OP", "=OP", { re: S`(?<!\w)Optimism(?!\w)`, cs: true, ctx: "crypto", head: "optimism" }],
  ["TIA", "celestia", "=TIA"],
  ["WLD", "worldcoin", "=WLD"],
  ["FARTCOIN", "fartcoin"],
  ["PUMP", "pump.fun", "pumpfun", "=PUMP"],
  ["TRUMP", { re: S`(?<!\w)(?:official )?trump (?:meme ?coins?|memecoins?|coins?|tokens?)(?!\w)`, head: "trump" }],
  ["MELANIA", { re: S`(?<!\w)melania (?:meme ?coins?|memecoins?|coins?|tokens?)(?!\w)`, head: "melania" }],
  ["WLFI", "world liberty financial", "world liberty", "wlfi"],
  ["XMR", "monero", "=XMR"],
  ["ZEC", "zcash", "=ZEC"],
  ["TAO", "bittensor", "~TAO"],
  ["WIF", "dogwifhat", "=WIF"],
  ["BONK", "=BONK", "=kBONK"],
  ["FLOKI", "floki", "=kFLOKI"],
  ["INJ", "^Injective", "=INJ"],
  ["ONDO", "ondo finance", "=ONDO"],
  ["SEI", "=SEI", { re: S`(?<!\w)Sei(?=[\s-]+(?:[Nn]etwork|[Bb]lockchain|[Ff]oundation|[Ee]cosystem)\b)`, cs: true, head: "sei" }],
  ["JUP", "=JUP", { re: S`(?<!\w)jupiter(?!\w)`, ctx: "crypto", head: "jupiter" }],
  ["LDO", "lido dao", "lido finance", "=LDO"],
  ["PENDLE", "pendle"],
  ["FIL", "filecoin", "=FIL"],
  ["ICP", "internet computer", "=ICP"],
  ["ATOM", "~ATOM", { re: S`(?<!\w)cosmos(?!\w)`, ctx: "crypto", head: "cosmos" }],
  ["XLM", "=XLM", "stellar lumens", { re: S`(?<!\w)Stellar(?!\w)`, cs: true, ctx: "crypto", head: "stellar" }],
  ["HBAR", "hedera", "=HBAR"],
  ["KAS", "kaspa", "=KAS"],
  ["ALGO", "algorand", "=ALGO"],
  ["BERA", "berachain", "=BERA"],
  ["PENGU", "pudgy penguins", "=PENGU"],
  ["ASTER", "=ASTER"],
  ["XPL", "=XPL"],
  ["MON", "~MON", { re: S`(?<!\w)Monad(?!\w)`, cs: true, ctx: "crypto", head: "monad" }],
  ["ZRO", "layerzero", "=ZRO"],
  ["EIGEN", "eigenlayer", "=EIGEN"],
  ["RENDER", "render network", "=RENDER"],
  ["FET", "fetch.ai", "=FET"],
  ["VIRTUAL", "virtuals protocol"],
  ["MOODENG", "=MOODENG"],
  ["POPCAT", "popcat"],
  ["SPX", "spx6900"],
  ["PURR", "=PURR"],

  /* ── commodities ── */
  ["XAU", "gold", "=XAU", "xauusd", "xau/usd", "paxg", "pax gold", "xaut", "tether gold"],
  ["XAG", "silver", "=XAG", "xag/usd"],
  ["XCU", "copper", "=XCU"],
  ["XPT", "platinum", "=XPT"],
  ["XPD", "palladium", "=XPD"],
  ["ALUMINIUM", "aluminium", "aluminum"],
  ["CL", "oil", "crude", "crude oil", "wti", "west texas intermediate", "=USOIL"],
  ["BZ", "brent", "brent crude", "brent oil", "=BRENTOIL", "=UKOIL"],
  ["NATGAS", "natural gas", "nat gas", "natgas", "henry hub"],
  ["TTF", "=TTF", "dutch gas", "european gas", "eu gas", "dutch ttf"],
  ["HO", "heating oil"],
  ["URANIUM", "uranium"],
  ["WHEAT", "wheat"],
  ["CORN", "corn"],
  ["SOY", "soybeans", "soybean", "soy"],
  ["COCOA", "cocoa"],
  ["COFFEE", "arabica", "robusta", { re: S`(?<!\w)coffee (?:prices?|futures|beans|crop|harvest|exports|output|market)(?!\w)`, head: "coffee" }],
  ["SUGAR", "raw sugar", { re: S`(?<!\w)sugar (?:prices?|futures|crop|harvest|exports|output|market)(?!\w)`, head: "sugar" }],
  ["COTTON", { re: S`(?<!\w)cotton (?:prices?|futures|crop|harvest|exports|output|market)(?!\w)`, head: "cotton" }],
  ["URNM", "=URNM"],
  ["URA", "=URA"],
  ["SLV", "=SLV"],
  ["USO", "=USO"],
  ["GDX", "=GDX"],

  /* ── indices and index funds ── */
  ["SP500", "s&p 500", "s&p500", "s&p futures", "s&p index", "=US500", "=SPY",
    { re: S`(?<!\w)s\s*&\s*p(?=\s+(?:closes|ends|opens|${MOVE}|record)\b)`, head: "s" }],
  ["NDX", "nasdaq", "nasdaq 100", "nasdaq composite", "=NDX", "=XYZ100", "=US100", "=USTECH", "=NAS100", "=QQQ"],
  ["DJI", "dow jones", "=DJIA", "=US30", "=DIA",
    { re: S`(?<!\w)the dow(?!\w)|(?<!\w)dow(?=\s+(?:futures|industrials|average|index|points|closes|ends|opens|record|loses|lost|sheds|shed|adds|added|${MOVE})\b)`, head: "dow" }],
  ["RUT", "russell 2000", "russell2000", "small caps", "small cap stocks", "=US2000", "=IWM"],
  ["N225", "nikkei", "nikkei 225", "=JP225", "=N225"],
  ["KOSPI", "kospi", "=KR200"],
  ["HSI", "hang seng", "=HSI"],
  ["DAX", "=DAX"],
  ["FTSE", "=FTSE", "ftse 100"],
  ["ESTX50", "euro stoxx 50", "euro stoxx", "stoxx 50"],
  ["CAC", "cac 40"],
  ["IBOV", "bovespa", "ibovespa"],
  ["NIFTY", "nifty 50", "nifty50", "=NIFTY", { re: S`(?<!\w)Nifty(?=\s+(?:index|futures|Bank|IT|closes|ends|${MOVE})\b)`, cs: true, head: "nifty" }],
  ["VIX", "vix", "fear gauge", "volatility index", "cboe volatility"],
  ["EWY", "=EWY"],
  ["EWJ", "=EWJ"],
  ["EWZ", "=EWZ"],
  ["EWT", "=EWT"],
  ["SMH", "=SMH"],
  ["XLE", "=XLE"],
  ["XBI", "=XBI"],
  ["SOXL", "=SOXL"],
  ["SOXX", "=SOXX"],
  ["TQQQ", "=TQQQ"],
  ["UVXY", "=UVXY"],
  ["KORU", "=KORU"],
  ["MAGS", "=MAGS"],
  ["KWEB", "=KWEB"],
  ["IGV", "=IGV"],

  /* ── currencies ── */
  ["EUR", { re: S`(?<!\w)euro(?![\w-])(?![\s-]+(?:zone|area)\b)`, head: "euro" }, "=EUR", "eur/usd", "eurusd"],
  ["JPY", "yen", "=JPY", "usd/jpy", "usdjpy"],
  ["GBP", "sterling", "british pound", "pound sterling", "=GBP", "gbp/usd", "gbpusd",
    { re: S`(?<!\w)the pound(?!\w)(?![\s-]+(?:of|for|sign|shop|store|key)\b)|(?<!\w)pound(?=\s+${MOVES}\b)`, head: "pound" }],
  ["CNH", "yuan", "renminbi", "=CNH", "=CNY", "=RMB", "usd/cnh", "usd/cny"],
  ["CHF", "swiss franc", "=CHF", "usd/chf"],
  ["AUD", "australian dollar", "aussie dollar", "=AUD", "aud/usd"],
  ["CAD", "canadian dollar", "loonie", "usd/cad"],
  ["NZD", "new zealand dollar", "kiwi dollar", "=NZD", "nzd/usd"],
  ["KRW", "korean won", "=KRW", "usd/krw"],
  ["HKD", "hong kong dollar", "=HKD"],
  ["DXY", "dollar index", "=DXY", "=USDX", "greenback", "u.s. dollar", "us dollar",
    { re: S`(?<!\w)the dollar(?!\w)(?![\s-]+(?:stores?|amounts?|values?|figures?|signs?)\b)|(?<!\w)dollar(?=\s+${MOVES}\b)`, head: "dollar" }],

  /* ── rates ── */
  ["US10Y", { re: S`${NOT_US}(?<!\w)(?:10|ten)[- ]year\s+(?:treasury|treasuries|yields?|notes?|t-notes?|bonds? yields?)(?!\w)`, head: "year" },
    "treasury yields", "treasury yield", "treasuries", "treasury market", "treasury auction", "t-note", "t-notes", "=US10Y", "=TNX"],
  ["US2Y", { re: S`${NOT_US}(?<!\w)(?:2|two)[- ]year\s+(?:treasury|treasuries|yields?|notes?)(?!\w)`, head: "year" }, "=US2Y"],
  ["US30Y", { re: S`${NOT_US}(?<!\w)(?:30|thirty)[- ]year\s+(?:treasury|treasuries|yields?|bonds?)(?!\w)`, head: "year" }, "long bond", "=US30Y"],
  ["TLT", "=TLT"],

  /* ── companies ── */
  ["NVDA", "nvidia", "=NVDA"],
  ["TSLA", "tesla", "=TSLA"],
  ["AAPL", "^Apple", "apple inc", "=AAPL"],
  ["MSFT", "microsoft", "=MSFT"],
  ["META", "^Meta", "meta platforms", "facebook", "=META"],
  ["AMZN", "^Amazon", "amazon.com", "=AMZN"],
  ["GOOGL", "google", "^Alphabet", "=GOOGL", "=GOOG"],
  ["COIN", "coinbase", "~COIN"],
  ["MSTR", "microstrategy", "strategy inc", "=MSTR",
    { re: S`(?<!\w)Strategy(?=\s+(?:Inc\b|\(MSTR\)|MSTR\b))|(?<=Saylor(?:'s|-led)?\s+)Strategy(?!\w)|(?<!\w)Strategy(?=(?:'s)?\s+(?:buys|bought|adds|added|acquires|acquired|purchases|purchased|sells|sold|holds|raises|scoops|snaps)\b.*\b(?:[Bb]itcoin|BTC)\b)`, cs: true, head: "strategy" }],
  ["CRCL", "=CRCL",
    { re: S`(?<!\w)Circle(?=\s+(?:Internet|Group|Inc\b|shares|stock|IPO|CRCL)|'s\s+(?:USDC|stablecoin|IPO|shares|stock))|(?<!\w)Circle\b(?=.*\b(?:USDC|[Ss]tablecoins?|CRCL)\b)|(?<=\b(?:USDC|[Ss]tablecoin)\s+(?:issuer|maker|firm|giant)\s+)Circle\b`, cs: true, head: "circle" }],
  ["PLTR", "palantir", "=PLTR"],
  ["AMD", "=AMD", "advanced micro devices"],
  ["AVGO", "broadcom", "=AVGO"],
  ["MU", "^Micron", "~MU"],
  ["SNDK", "sandisk", "=SNDK"],
  ["NFLX", "netflix", "=NFLX"],
  ["ORCL", "^Oracle", "=ORCL"],
  ["INTC", "intel corp", "chipmaker intel", "=INTC", { re: S`(?<!\w)(?:Intel|INTEL)(?!\w)`, cs: true, ctx: "intel", head: "intel" }],
  ["HOOD", "robinhood", "=HOOD"],
  ["COST", "costco", "~COST"],
  ["LLY", "eli lilly", "=LLY",
    { re: S`(?<!\w)Lilly(?='s|\s+(?:shares|stock|drug|pill|weight|obesity|earnings|sales|profit|revenue)\b)`, cs: true, head: "lilly" }],
  ["SKHYNIX", "sk hynix", "hynix", "=SKHX", "=SKHY"],
  ["TSM", "tsmc", "taiwan semiconductor", "=TSM"],
  ["RIVN", "rivian", "=RIVN"],
  ["BABA", "alibaba", "=BABA"],
  ["SAMSUNG", "samsung", "=SMSN"],
  ["GME", "gamestop", "=GME"],
  ["SOFTBANK", "softbank"],
  ["HYUNDAI", "hyundai"],
  ["KIOXIA", "kioxia"],
  ["HIMS", "hims & hers", "hims and hers", "=HIMS"],
  ["DKNG", "draftkings", "=DKNG"],
  ["LITE", "lumentum", "~LITE"],
  ["BX", "blackstone", "~BX"],
  ["MRVL", "marvell", "=MRVL"],
  ["RKLB", "rocket lab", "=RKLB"],
  ["CBRS", "cerebras", "=CBRS"],
  ["ZM", "zoom video", "zoom communications", "~ZM",
    { re: S`(?<!\w)Zoom(?=\s+(?:shares|stock|earnings|revenue)\b)`, cs: true, head: "zoom" }],
  ["EBAY", "ebay"],
  ["ARM", "arm holdings", "~ARM", { re: S`(?<!\w)arm(?=\s+(?:holdings|architecture|chips?|shares|stock|ltd|plc)\b)`, head: "arm" }],
  ["SPCX", "spacex", "=SPCX"],
  ["ASML", "asml"],
  ["MINIMAX", "^MiniMax"],
  ["BB", "^BlackBerry", "~BB"],
  ["DELL", "^Dell", "dell technologies", "=DELL"],
  ["IBM", "=IBM"],
  ["NOW", "servicenow", "~NOW"],
  ["NBIS", "nebius", "=NBIS"],
  ["WDC", "western digital", "=WDC"],
  ["NOK", "nokia", "~NOK"],
  ["BE", "bloom energy", "~BE"],
  ["ZHIPU", "zhipu"],
  ["QCOM", "qualcomm", "=QCOM"],
  ["STRC", "=STRC"],
  ["AMAT", "applied materials", "=AMAT"],
  ["GIGADEV", "gigadevice"],
  ["CXMT", "changxin memory", "=CXMT"],
  ["GEV", "ge vernova", "=GEV"],
  ["UNITREE", "unitree"],
  ["IREN", "=IREN", "iris energy"],
  ["NET", "cloudflare", "~NET"],
  ["CRWD", "crowdstrike", "=CRWD"],
  ["RDDT", "^Reddit", "=RDDT"],
  ["AAOI", "applied optoelectronics", "=AAOI"],
  ["MRNA", "moderna", "=MRNA"],
  ["SHEIN", "shein"],
  ["BMNR", "bitmine", "=BMNR"],
  ["CVX", "chevron", "=CVX"],
  ["LRCX", "lam research", "=LRCX"],
  ["UMC", "united microelectronics", "=UMC"],
  ["TWST", "twist bioscience", "=TWST"],
  ["GLW", "^Corning", "=GLW"],
  ["OURA", "^Oura"],
  ["USAR", "usa rare earth", "=USAR"],
  ["COHR", "=COHR"],
  ["CRDO", "credo technology", "=CRDO"],
  ["STX", "seagate"],
  ["VST", "vistra", "=VST"],
  ["TER", "teradyne"],
  ["CIEN", "ciena", "=CIEN"],
  ["MELI", "mercadolibre", "mercado libre", "=MELI"],
  ["SMCI", "super micro", "supermicro", "=SMCI"],
  ["SOFI", "^SoFi", "=SOFI"],
  ["TTWO", "take-two", "take two interactive", "=TTWO"],
  ["CIFR", "cipher mining", "=CIFR"],
  ["IONQ", "ionq"],
  ["GPRO", "gopro", "=GPRO"],
  ["OPENAI", "openai"],
  ["ANTHROPIC", "^Anthropic"],
  ["TENCENT", "tencent"],
  ["XIAOMI", "xiaomi"],
  ["RTX", "raytheon", "=RTX"],
  ["ADBE", "^Adobe", "=ADBE"],
  ["CRM", "salesforce", "~CRM"],
  ["CSCO", "cisco", "=CSCO"],
  ["DIS", "disney", "~DIS"],
  ["JPM", "jpmorgan", "jp morgan", "j.p. morgan", "=JPM"],
  ["UBER", "^Uber", "=UBER"],
  ["V", { re: S`(?<!\w)Visa(?=\s+(?:Inc\b|shares|stock|earnings|revenue|profit|and Mastercard)|'s\s+(?:shares|stock|earnings|revenue|profit|CEO))|(?<=Mastercard(?:,| and)\s+)Visa\b`, cs: true, head: "visa" }],
  ["WMT", "walmart", "=WMT"],
  ["HD", "home depot"],
  ["NVO", "novo nordisk", "=NVO"],
  ["QBTS", "d-wave", "=QBTS"],
  ["RGTI", "rigetti", "=RGTI"],
  ["ASTS", "ast spacemobile", "=ASTS"],
  ["BYD", "=BYD"],
  ["PDD", "pinduoduo", "=PDD"],
  ["CRWV", "coreweave", "=CRWV"],
  ["CATL", "=CATL", "contemporary amperex"],
  ["RELIANCE", "reliance industries"],
  ["ACN", "accenture", "=ACN"],
  ["CAMBRICON", "cambricon"],
  ["YMTC", "=YMTC", "yangtze memory"],
  ["INNOLIGHT", "innolight"],
  ["IBIDEN", "ibiden"],
];

/* ── compiled once ────────────────────────────────────────────────── */

type Matcher = { sym: string; re: RegExp; upper: boolean; ctx: RegExp | null };

const BY_HEAD = new Map<string, Matcher[]>();
const ALWAYS: Matcher[] = [];

function add(head: string | null, m: Matcher): void {
  if (!head) {
    ALWAYS.push(m);
    return;
  }
  const at = BY_HEAD.get(head);
  if (at) at.push(m);
  else BY_HEAD.set(head, [m]);
}

function compile(sym: string, spec: Spec): void {
  const coin = classOf(sym) === "crypto";
  if (typeof spec !== "string") {
    const ctx = spec.ctx ? CONTEXT[spec.ctx] : null;
    add(spec.head ?? null, { sym, re: new RegExp(spec.re, spec.cs ? "g" : "gi"), upper: spec.upper === true, ctx });
    return;
  }
  const mark = spec[0];
  if (mark === "=" || mark === "~") {
    const tick = spec.slice(1);
    const ctx = mark === "~" ? CONTEXT[coin ? "crypto" : "equity"] : null;
    const head = tick.toLowerCase();
    add(head, { sym, re: new RegExp(`(?<!\\w)${tick}(?!\\w)`, "g"), upper: true, ctx });
    if (coin) {
      // "SOL token", "sol coin"; "SOL price", "Sol price"
      add(head, { sym, re: new RegExp(`(?<!\\w)${tick}(?=[\\s-]+(?:token|coin)s?(?!\\w))`, "gi"), upper: false, ctx: null });
      const cap = tick.charAt(0) + tick.slice(1).toLowerCase();
      add(head, { sym, re: new RegExp(`(?<!\\w)(?:${tick}|${cap})(?=\\s+price)`, "g"), upper: false, ctx });
    }
    return;
  }
  if (mark === "^") {
    const name = spec.slice(1);
    add(headOf(name), { sym, re: new RegExp(`${phraseSource(name)}|${phraseSource(name.toUpperCase())}`, "g"), upper: false, ctx: null });
    return;
  }
  add(headOf(spec), { sym, re: new RegExp(phraseSource(spec), "gi"), upper: false, ctx: null });
}

for (const [sym, ...specs] of NAMES) {
  const canon = canonical(sym);
  for (const spec of specs) compile(canon, spec);
}

/** Cashtags that are dollars, not underlyings. */
const NOT_CASHTAGS = new Set(["USD", "USDT", "USDC", "USDE", "USDS", "US", "DAI", "PYUSD", "FDUSD"]);

type Hit = { at: number; end: number; sym: string };

/** The underlyings a headline names, canonical, in the order it names them. */
export function tickersIn(r: Reading): string[] {
  const { text } = r;
  const hits: Hit[] = [];
  const run = (m: Matcher): void => {
    if (m.upper && r.shout) return;
    if (m.ctx && !m.ctx.test(text)) return;
    m.re.lastIndex = 0;
    for (let x = m.re.exec(text); x !== null; x = m.re.exec(text)) {
      if (x[0].length === 0) {
        m.re.lastIndex++;
        continue;
      }
      hits.push({ at: x.index, end: x.index + x[0].length, sym: m.sym });
    }
  };
  for (const word of wordsOf(r.lower)) {
    const list = BY_HEAD.get(word);
    if (list) for (const m of list) run(m);
  }
  for (const m of ALWAYS) run(m);
  for (const x of text.matchAll(/\$([A-Za-z][A-Za-z0-9]{1,9})(?!\w)/g)) {
    const tag = x[1] ?? "";
    if (NOT_CASHTAGS.has(tag.toUpperCase())) continue;
    hits.push({ at: x.index, end: x.index + x[0].length, sym: canonical(tag) });
  }
  // the earliest first, the longest of those; then nothing that overlaps what was taken
  hits.sort((a, b) => a.at - b.at || b.end - a.end);
  const out: string[] = [];
  let reach = 0;
  for (const hit of hits) {
    if (hit.at < reach) continue;
    reach = hit.end;
    if (!out.includes(hit.sym)) out.push(hit.sym);
  }
  return out;
}

/* ── atlas's ticker_mentions ──────────────────────────────────────── */

/** Upper-case words in news that are not tickers (atlas `NOT_TICKERS`). */
const NOT_TICKERS = new Set(
  (
    "US USA UK EU UN UAE NATO OPEC BRICS G7 G20 USSR DRC PRC NYC LA DC " +
    "CEO CFO CTO COO FBI CIA NSA WHO IMF ECB FED SEC IRS DOJ FDA EPA NASA OSHA GDP CPI PPI PMI USGS GDACS ACLED " +
    "GDELT NOAA FEMA ICE TSA AP BBC CNN AFP PTI ANI TASS RT TV NPR AM PM ET PT UTC GMT EST PST KM MI FT LB KG MW GW " +
    "KW BBL MPH KPH AND THE FOR NEW NOT ALL WAS HAS OUT OFF TOP LIVE BREAKING UPDATE EXCLUSIVE WATCH VIDEO PHOTO " +
    "ALERT WARNING REPORT NEWS WORLD MORE SAYS WILL ETF IPO IOU APR APY ATH ATL YOY QOQ EPS"
  ).split(" "),
);

/** What a headline can be said to mention as tickers, as written, upper-case, in order: its
 * `$cashtags`, then words of three or more capitals that are not known acronyms (none when the
 * headline is shouted). A port of atlas `ticker_mentions_in_order`: for matching against a live
 * listing (run each through `canonical`), where the names table above does not reach. */
export function tickerMentions(headline: string): string[] {
  const out: string[] = [];
  const add = (t: string): void => {
    if (!out.includes(t)) out.push(t);
  };
  for (const x of headline.matchAll(/\$([A-Za-z][A-Za-z0-9]{1,9})(?!\w)/g)) add((x[1] ?? "").toUpperCase());
  if (shouted(headline)) return out;
  for (const raw of headline.split(/[^A-Za-z0-9$]+/)) {
    const tok = raw.startsWith("$") ? raw.slice(1) : raw;
    if (tok.length >= 3 && /^[A-Z][A-Z0-9]*$/.test(tok) && !NOT_TICKERS.has(tok)) add(tok);
  }
  return out;
}
